import React, { ReactElement } from 'react';
import { useFormikContext, ErrorMessage, getIn } from 'formik';

import { formNameToNice } from '../utils';
import { themeColors } from '../globals';

import './GroupedMultipleSelect.scss';

interface Elem {
  id: number
}

interface ownProps<T extends Elem, G extends string> {
  name: string,
  displayName?: string,
  help_text?: string | ReactElement,
  elements: T[],
  getGroup: (e: T) => G,
  groupToColor: (g: G) => string,
  displayGroup?: (g: G) => string,
  displayElem: (e: T) => string,
}

const GroupedMultipleSelect = <T extends Elem, G extends string>(props: ownProps<T, G>) => {
  const { displayName, name, elements, getGroup, groupToColor, displayGroup, displayElem } = props;
  const { values, setFieldValue } = useFormikContext();
  const selected = (getIn(values, name) || []) as number[];
  const niceName = displayName || formNameToNice(name);
  const groups = [...new Set(elements.map(getGroup))].sort();

  const toggle = (id: number) => {
    if (!id) return;
    setFieldValue(name, selected.includes(id) ? selected.filter(value => value !== id) : [...selected, id]);
  };

  return (
    <div className="form-group row grouped-multiple-select" role="group"
      aria-labelledby={`${name}-label`} aria-describedby={props.help_text ? `${name}-help` : undefined}>
      <div className="col-sm-2 col-form-label">
        <div id={`${name}-label`}>{niceName}</div>
        <small className="text-muted">{selected.length} selezionati</small>
      </div>
      <div className="col-sm">
        <div className="grouped-select-groups">
          {groups.map(group => {
            const members = elements.filter(element => getGroup(element) === group);
            const selectedCount = members.filter(element => selected.includes(element.id)).length;
            const groupLabel = displayGroup ? displayGroup(group) : group;
            return (
              <div key={group} className="grouped-select-group" role="group"
                aria-labelledby={`${name}-${group}-label`}
                style={{ '--group-color': themeColors[groupToColor(group)] } as React.CSSProperties}>
                <div className="grouped-select-header">
                  <span className="grouped-select-title" id={`${name}-${group}-label`}>
                    <span className="grouped-select-dot" aria-hidden="true" />{groupLabel}
                  </span>
                  <span className="grouped-select-count" aria-label={`${selectedCount} di ${members.length} selezionati`}>
                    {selectedCount} / {members.length}
                  </span>
                </div>
                <div className="grouped-select-options">
                  {members.map(element => (
                    <label key={element.id} className="grouped-select-option">
                      <input name={name} type="checkbox" value={element.id}
                        checked={selected.includes(element.id)} onChange={() => toggle(element.id)} />
                      <span className="grouped-select-pill">
                        <span className="grouped-select-check" aria-hidden="true"><i className="fa fa-check" /></span>
                        {displayElem(element)}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        {props.help_text && <div id={`${name}-help`} className="form-text text-muted small">{props.help_text}</div>}
        <ErrorMessage name={name} component="small" className="text-danger text-small" />
      </div>
    </div>
  );
};

export default GroupedMultipleSelect;
